/**
 * Voice Commands System
 * 
 * Provides hands-free control of the app using Web Speech API.
 * Designed for seniors who may have difficulty with touch interfaces.
 * 
 * Features:
 * - Navigation commands ("go to home", "open games")
 * - Action commands ("check in", "go back")
 * - Natural language processing for flexible command matching
 * - Visual and audio feedback
 */

// Check if Web Speech API is supported
export const isSpeechRecognitionSupported = (): boolean => {
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

// Get the SpeechRecognition constructor
const getSpeechRecognition = (): typeof SpeechRecognition | null => {
  if ('SpeechRecognition' in window) {
    return (window as any).SpeechRecognition;
  }
  if ('webkitSpeechRecognition' in window) {
    return (window as any).webkitSpeechRecognition;
  }
  return null;
};

// Command types
export type VoiceCommandType = 
  | 'navigation'
  | 'action'
  | 'system';

export interface VoiceCommand {
  patterns: string[];
  action: string;
  type: VoiceCommandType;
  description: string;
}

// Define available commands
export const VOICE_COMMANDS: VoiceCommand[] = [
  // Navigation commands
  { patterns: ['go home', 'home', 'go to home', 'main menu', 'start'], action: 'navigate:/', type: 'navigation', description: 'Go to home page' },
  { patterns: ['chat', 'open chat', 'go to chat', 'talk', 'companion', 'lets chat', 'talk to me'], action: 'navigate:/chat', type: 'navigation', description: 'Open AI chat' },
  { patterns: ['games', 'open games', 'go to games', 'play', 'brain games', 'play games', 'lets play'], action: 'navigate:/games', type: 'navigation', description: 'Open brain games' },
  { patterns: ['reminders', 'open reminders', 'go to reminders', 'my reminders', 'alerts', 'show reminders', 'what do i need to do'], action: 'navigate:/reminders', type: 'navigation', description: 'Open reminders' },
  { patterns: ['mood', 'open mood', 'how am i', 'mood tracker', 'feelings', 'how do i feel', 'log mood', 'track mood'], action: 'navigate:/mood', type: 'navigation', description: 'Open mood tracker' },
  { patterns: ['exercises', 'open exercises', 'workout', 'exercise', 'stretch', 'lets exercise', 'do exercises', 'start workout'], action: 'navigate:/exercises', type: 'navigation', description: 'Open exercises' },
  { patterns: ['settings', 'open settings', 'preferences', 'options', 'change settings'], action: 'navigate:/settings', type: 'navigation', description: 'Open settings' },
  { patterns: ['wisdom', 'daily wisdom', 'quotes', 'inspiration', 'inspire me', 'give me wisdom', 'motivate me'], action: 'navigate:/wisdom', type: 'navigation', description: 'Open daily wisdom' },
  { patterns: ['health', 'health resources', 'emergency numbers', 'help numbers', 'medical help', 'doctor info'], action: 'navigate:/health', type: 'navigation', description: 'Open health resources' },
  { patterns: ['family', 'family sharing', 'share with family', 'my family', 'contact family'], action: 'navigate:/family', type: 'navigation', description: 'Open family sharing' },
  { patterns: ['about', 'about us', 'information', 'about this app', 'who made this'], action: 'navigate:/about', type: 'navigation', description: 'Open about page' },
  { patterns: ['hobbies', 'my hobbies', 'interests', 'things i like', 'show hobbies'], action: 'navigate:/hobbies', type: 'navigation', description: 'Open hobbies' },
  { patterns: ['voice tutorial', 'voice help', 'voice commands', 'how to use voice', 'teach me voice'], action: 'navigate:/voice-tutorial', type: 'navigation', description: 'Open voice tutorial' },
  { patterns: ['dashboard', 'overview', 'summary', 'my dashboard'], action: 'navigate:/dashboard', type: 'navigation', description: 'Open dashboard' },
  { patterns: ['privacy', 'privacy policy', 'my privacy', 'data privacy'], action: 'navigate:/privacy', type: 'navigation', description: 'Open privacy policy' },
  { patterns: ['terms', 'terms of service', 'legal', 'terms and conditions'], action: 'navigate:/terms', type: 'navigation', description: 'Open terms of service' },
  
  // Action commands
  { patterns: ['go back', 'back', 'previous', 'return', 'go backwards'], action: 'action:back', type: 'action', description: 'Go back to previous page' },
  { patterns: ['scroll down', 'down', 'next', 'page down', 'show more', 'more'], action: 'action:scrollDown', type: 'action', description: 'Scroll down' },
  { patterns: ['scroll up', 'up', 'page up', 'go up'], action: 'action:scrollUp', type: 'action', description: 'Scroll up' },
  { patterns: ['scroll to top', 'go to top', 'top of page', 'beginning'], action: 'action:scrollTop', type: 'action', description: 'Scroll to top of page' },
  { patterns: ['scroll to bottom', 'go to bottom', 'bottom of page', 'end'], action: 'action:scrollBottom', type: 'action', description: 'Scroll to bottom of page' },
  { patterns: ['check in', 'done', 'complete', 'finished', 'mark done', 'task complete'], action: 'action:checkIn', type: 'action', description: 'Mark reminder as done' },
  { patterns: ['call 911', 'emergency', 'help me', 'call emergency', 'i need help', 'sos'], action: 'action:emergency', type: 'action', description: 'Call emergency services' },
  { patterns: ['refresh', 'reload', 'refresh page', 'reload page'], action: 'action:refresh', type: 'action', description: 'Refresh the page' },
  { patterns: ['read aloud', 'read this', 'speak', 'read to me', 'read page'], action: 'action:readAloud', type: 'action', description: 'Read the page content aloud' },
  { patterns: ['larger text', 'bigger text', 'increase font', 'zoom in', 'make bigger'], action: 'action:increaseFontSize', type: 'action', description: 'Make text larger' },
  { patterns: ['smaller text', 'decrease font', 'zoom out', 'make smaller'], action: 'action:decreaseFontSize', type: 'action', description: 'Make text smaller' },
  { patterns: ['dark mode', 'night mode', 'turn on dark', 'dark theme'], action: 'action:darkMode', type: 'action', description: 'Switch to dark mode' },
  { patterns: ['light mode', 'day mode', 'turn on light', 'light theme', 'bright mode'], action: 'action:lightMode', type: 'action', description: 'Switch to light mode' },
  { patterns: ['whats the time', 'what time is it', 'current time', 'tell me the time'], action: 'action:tellTime', type: 'action', description: 'Tell current time' },
  { patterns: ['whats the date', 'what day is it', 'current date', 'tell me the date', 'todays date'], action: 'action:tellDate', type: 'action', description: 'Tell current date' },
  { patterns: ['new reminder', 'add reminder', 'create reminder', 'set reminder', 'remind me'], action: 'action:addReminder', type: 'action', description: 'Add a new reminder' },
  { patterns: ['start game', 'play game', 'new game', 'begin game'], action: 'action:startGame', type: 'action', description: 'Start a new game' },
  { patterns: ['good morning', 'morning', 'hello', 'hi'], action: 'action:greeting', type: 'action', description: 'Respond to greeting' },
  { patterns: ['good night', 'goodnight', 'bye', 'goodbye', 'see you'], action: 'action:farewell', type: 'action', description: 'Respond to farewell' },
  { patterns: ['thank you', 'thanks', 'appreciate it'], action: 'action:thanks', type: 'action', description: 'Acknowledge thanks' },
  
  // System commands
  { patterns: ['stop listening', 'stop', 'quiet', 'silence', 'stop voice', 'mute'], action: 'system:stopListening', type: 'system', description: 'Stop voice recognition' },
  { patterns: ['start listening', 'listen', 'wake up', 'turn on voice', 'unmute'], action: 'system:startListening', type: 'system', description: 'Start voice recognition' },
  { patterns: ['help', 'what can i say', 'commands', 'voice help', 'show commands', 'list commands'], action: 'system:help', type: 'system', description: 'Show available commands' },
  { patterns: ['repeat', 'say again', 'what did you say', 'pardon'], action: 'system:repeat', type: 'system', description: 'Repeat last response' },
  { patterns: ['speak slower', 'slow down', 'slower please'], action: 'system:speakSlower', type: 'system', description: 'Speak responses slower' },
  { patterns: ['speak faster', 'speed up', 'faster please'], action: 'system:speakFaster', type: 'system', description: 'Speak responses faster' },
  { patterns: ['volume up', 'louder', 'increase volume', 'speak louder'], action: 'system:volumeUp', type: 'system', description: 'Increase volume' },
  { patterns: ['volume down', 'quieter', 'decrease volume', 'speak quieter'], action: 'system:volumeDown', type: 'system', description: 'Decrease volume' },
];

// Preference storage
const VOICE_ENABLED_KEY = 'dipps_voice_enabled';
const VOICE_LANGUAGE_KEY = 'dipps_voice_language';
const VOICE_CONTINUOUS_KEY = 'dipps_voice_continuous';

export const getVoiceEnabled = (): boolean => {
  try {
    const stored = localStorage.getItem(VOICE_ENABLED_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
};

export const setVoiceEnabled = (enabled: boolean): void => {
  try {
    localStorage.setItem(VOICE_ENABLED_KEY, String(enabled));
  } catch {
    // Silently fail
  }
};

export const getVoiceLanguage = (): string => {
  try {
    return localStorage.getItem(VOICE_LANGUAGE_KEY) || 'en-US';
  } catch {
    return 'en-US';
  }
};

export const setVoiceLanguage = (language: string): void => {
  try {
    localStorage.setItem(VOICE_LANGUAGE_KEY, language);
  } catch {
    // Silently fail
  }
};

export const getContinuousListening = (): boolean => {
  try {
    const stored = localStorage.getItem(VOICE_CONTINUOUS_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
};

export const setContinuousListening = (enabled: boolean): void => {
  try {
    localStorage.setItem(VOICE_CONTINUOUS_KEY, String(enabled));
  } catch {
    // Silently fail
  }
};

// Match spoken text to a command
export const matchCommand = (transcript: string): VoiceCommand | null => {
  const normalizedTranscript = transcript.toLowerCase().trim();
  
  for (const command of VOICE_COMMANDS) {
    for (const pattern of command.patterns) {
      // Exact match or contains match
      if (normalizedTranscript === pattern || 
          normalizedTranscript.includes(pattern) ||
          pattern.includes(normalizedTranscript)) {
        return command;
      }
    }
  }
  
  // Fuzzy matching for partial commands
  for (const command of VOICE_COMMANDS) {
    for (const pattern of command.patterns) {
      const patternWords = pattern.split(' ');
      const transcriptWords = normalizedTranscript.split(' ');
      
      // Check if most words match
      const matchCount = patternWords.filter(word => 
        transcriptWords.some(tWord => 
          tWord.includes(word) || word.includes(tWord)
        )
      ).length;
      
      if (matchCount >= Math.ceil(patternWords.length * 0.7)) {
        return command;
      }
    }
  }
  
  return null;
};

// Event types
export type VoiceEventType = 
  | 'start'
  | 'end'
  | 'result'
  | 'error'
  | 'command'
  | 'noMatch';

export interface VoiceEvent {
  type: VoiceEventType;
  transcript?: string;
  command?: VoiceCommand;
  error?: string;
  confidence?: number;
}

export type VoiceEventHandler = (event: VoiceEvent) => void;

// Voice recognition instance
class VoiceCommandEngine {
  private recognition: SpeechRecognition | null = null;
  private isListening: boolean = false;
  private handlers: VoiceEventHandler[] = [];
  private language: string = 'en-US';
  private continuous: boolean = false;

  constructor() {
    // Load continuous mode preference
    this.continuous = getContinuousListening();
    this.initialize();
  }

  private initialize(): void {
    const SpeechRecognitionClass = getSpeechRecognition();
    if (!SpeechRecognitionClass) return;

    this.recognition = new SpeechRecognitionClass();
    this.recognition.continuous = this.continuous;
    this.recognition.interimResults = false;
    this.recognition.lang = this.language;
    this.recognition.maxAlternatives = 3;

    this.recognition.onstart = () => {
      this.isListening = true;
      this.emit({ type: 'start' });
    };

    this.recognition.onend = () => {
      this.isListening = false;
      this.emit({ type: 'end' });
      
      // Auto-restart if continuous mode
      if (this.continuous && getVoiceEnabled()) {
        setTimeout(() => this.start(), 500);
      }
    };

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      const result = event.results[event.results.length - 1];
      const transcript = result[0].transcript;
      const confidence = result[0].confidence;

      this.emit({ type: 'result', transcript, confidence });

      const command = matchCommand(transcript);
      if (command) {
        this.emit({ type: 'command', command, transcript, confidence });
      } else {
        this.emit({ type: 'noMatch', transcript, confidence });
      }
    };

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      this.emit({ type: 'error', error: event.error });
      
      // Don't restart on certain errors
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        this.isListening = false;
      }
    };
  }

  private emit(event: VoiceEvent): void {
    this.handlers.forEach(handler => handler(event));
  }

  subscribe(handler: VoiceEventHandler): () => void {
    this.handlers.push(handler);
    return () => {
      this.handlers = this.handlers.filter(h => h !== handler);
    };
  }

  start(): boolean {
    if (!this.recognition) {
      this.emit({ type: 'error', error: 'Speech recognition not supported' });
      return false;
    }

    if (this.isListening) return true;

    try {
      this.recognition.lang = getVoiceLanguage();
      this.recognition.start();
      return true;
    } catch (error) {
      this.emit({ type: 'error', error: 'Failed to start recognition' });
      return false;
    }
  }

  stop(): void {
    if (!this.recognition || !this.isListening) return;
    
    try {
      this.recognition.stop();
    } catch {
      // Ignore errors
    }
  }

  abort(): void {
    if (!this.recognition) return;
    
    try {
      this.recognition.abort();
    } catch {
      // Ignore errors
    }
    this.isListening = false;
  }

  isActive(): boolean {
    return this.isListening;
  }

  setContinuous(continuous: boolean): void {
    this.continuous = continuous;
    setContinuousListening(continuous); // Save preference
    if (this.recognition) {
      this.recognition.continuous = continuous;
    }
  }

  isContinuous(): boolean {
    return this.continuous;
  }

  setLanguage(language: string): void {
    this.language = language;
    setVoiceLanguage(language);
    if (this.recognition) {
      this.recognition.lang = language;
    }
  }
}

// Singleton instance
let engineInstance: VoiceCommandEngine | null = null;

export const getVoiceEngine = (): VoiceCommandEngine => {
  if (!engineInstance) {
    engineInstance = new VoiceCommandEngine();
  }
  return engineInstance;
};

// Supported languages for voice recognition
export const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'es-ES', name: 'Spanish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'de-DE', name: 'German' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'tr-TR', name: 'Turkish' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'hi-IN', name: 'Hindi' },
  { code: 'ar-SA', name: 'Arabic' },
];

// Export types for TypeScript
export type { VoiceCommandEngine };
